<template>
  <div class="lifestyleMatrix">
    <v-alert v-if="!lifestyle.length" outlined dense type="info">
      Please, select lifestyle on the previous tab
    </v-alert>
    <v-alert v-else-if="lifestyleId === 0" outlined dense type="info">
      Please, save new lifestyle on the previous tab before accessing matrix
    </v-alert>
    <div v-else>
      <div v-if="loading">
        <v-progress-circular
          :size="20"
          color="primary"
          class="mr-2"
          indeterminate
        ></v-progress-circular>
        Loading...
      </div>

      <div v-else>
        <div v-if="!loading && !matrixList.length">
          <v-alert type="info" outlined dense> No matrix for this lifestyle yet </v-alert>
        </div>

        <div v-else>
          <v-simple-table :style="{ maxWidth: maxTableWidth + 'px' }">
            <tr>
              <th class="bd-b"></th>
              <th class="bd-b">YTR</th>
              <th v-for="(headercell, inx) in matrixList[0]" :key="inx" class="bd-b">
                {{ headercell.ManagerName + ': ' + headercell.FundName }}
                <v-icon v-if="showFormButtons" small @click="deleteColumn(headercell.ColIndex)"
                  >mdi-delete</v-icon
                >
              </th>
            </tr>
            <tr v-for="(row, index) in matrixList" :key="index" @click="editRow(row, index)">
              <td class="bd-b">
                <v-icon v-if="showFormButtons" @click.stop="deleteRow(index)">mdi-delete</v-icon>
              </td>
              <td class="bd-b">{{ row[0].AgeToRetirement }}</td>
              <td v-for="(cell, i) in row" :key="i" class="bd-b">{{ cell.Percent }}</td>
            </tr>
          </v-simple-table>
        </div>

        <!-- Add row dialog -->
        <v-dialog
          v-model="showDialog"
          width="550"
          @keydown.esc="showDialog = false"
          @keydown.enter="saveRow"
          persistent
        >
          <v-card>
            <v-card-text class="pt-5 pb-0">
              <v-container>
                <v-form ref="rowForm" v-model="isRowValid" lazy-validation>
                  <v-row>
                    <v-col cols="4">
                      <v-text-field
                        v-model="editedRowYTR"
                        type="number"
                        label="Years to retirement"
                        :rules="[(v) => !!v || 'Required.']"
                        min="0.1"
                        step="0.1"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4" v-for="(editedCell, index) in editedRow" :key="index">
                      <v-text-field
                        v-model="editedCell.Percent"
                        type="number"
                        :label="editedCell.ManagerName + ':' + editedCell.FundName"
                        min="0"
                        max="100"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row
                    :style="{
                      height: '22px',
                    }"
                  >
                    <span v-if="showRowError" class="red--text"
                      >* Sum of all funds should be equal to 100%
                    </span>
                  </v-row>
                </v-form>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-btn v-if="this.editedRowIndex !== -1" color="error" outlined @click="deleteRow">
                Delete
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" outlined @click="showDialog = false"> Cancel </v-btn>
              <v-btn color="primary" depressed @click="saveRow" :disabled="showRowError">
                Ok
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Add column dialog -->
        <v-dialog
          v-model="showColumnDialog"
          width="550"
          @keydown.esc="showColumnDialog = false"
          @keydown.enter="addColumn"
          persistent
        >
          <v-card>
            <v-card-text class="pt-5 pb-0">
              <v-container>
                <v-form ref="colForm" v-model="isNewColumnValid" lazy-validation>
                  <v-row>
                    <v-col cols="6">
                      <v-select
                        v-model="managerId"
                        :items="managerList"
                        item-text="ManagerName"
                        item-value="ManagerId"
                        label="Manager"
                        :loading="isManagerIdLoading"
                        :rules="rules"
                      ></v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        v-model="fundId"
                        :items="fundList"
                        item-value="PortfolioId"
                        item-text="PortfolioName"
                        label="Fund"
                        :loading="isFundIdLoading"
                        :rules="rules"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" outlined @click="showColumnDialog = false"> Cancel </v-btn>
              <v-btn color="primary" depressed @click="addColumn"> Add </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <Dialog ref="confirmationDialog" v-model="dialog" />
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';

export default {
  name: 'LifestyleMatrix',
  components: { Dialog },
  props: {
    lifestyle: Array,
    addColCount: Number,
    addRowCount: Number,
    saveMatrixCount: Number,
    showFormButtons: Boolean,
  },

  data() {
    return {
      loading: false,
      matrixList: [],
      showDialog: false,
      editedRow: [],
      editedRowIndex: -1,
      isRowValid: true,
      editedRowYTR: 0,
      showColumnDialog: false,
      managerList: [],
      managerId: null,
      isManagerIdLoading: false,
      fundList: [],
      fundId: null,
      isFundIdLoading: false,
      rules: [],
      dialog: false,
      isNewColumnValid: false,
      maxColWidth: 280,
    };
  },

  computed: {
    lifestyleId() {
      if (this.lifestyle.length) return this.lifestyle[0].LifestyleId;
      return null;
    },

    headers() {
      const result = [{ text: 'YTR', value: '' }];
      if (this.matrixList.length) {
        this.matrixList[0].forEach((item) => {
          result.push({
            text: item.ManagerName + ': ' + item.FundName,
            value: item.Percent,
          });
        });
      }
      return result;
    },

    defaultRow() {
      if (this.matrixList.length) {
        return this.matrixList[this.matrixList.length - 1].map((cell) => {
          return {
            FundName: cell.FundName,
            ManagerId: cell.ManagerId,
            ManagerName: cell.ManagerName,
            ColIndex: cell.ColIndex,
            RowIndex: this.matrixList.length + 1,
            LifeStyleRowId: 0,
            LifestyleId: cell.LifestyleId,
            FundId: cell.FundId,
            Percent: Math.floor(100 / this.matrixList[0].length),
            AgeToRetirement: this.matrixList.length + 1,
          };
        });
      }
      return [];
    },

    areAllRowsValid() {
      return this.matrixList.every((row) => {
        return row.reduce((total, current) => (total += Number(current.Percent)), 0) === 100;
      });
    },

    areAllYearsDifferent() {
      const uniqueYTR = new Set();
      const flatMatrix = this.matrixList.flat();
      flatMatrix.forEach((el) => uniqueYTR.add(el.AgeToRetirement));
      return uniqueYTR.size === this.matrixList.length;
    },

    showRowError() {
      return !this.checkRowValues(this.editedRow);
    },

    maxTableWidth() {
      switch (this.matrixList[0].length) {
        case 0:
          return this.maxColWidth;
        case 1:
          return this.maxColWidth + 150;
        case 2:
        case 3:
          return (this.maxColWidth + 150) * this.matrixList[0].length;
        default:
          return this.maxColWidth * this.matrixList[0].length;
      }
    },

    isSaveDisabled() {
      return !(this.areAllRowsValid && this.areAllYearsDifferent && this.matrixList.length);
    },

    isAddRowDisabled() {
      return !this.matrixList.length;
    },
  },

  watch: {
    lifestyleId() {
      if (this.lifestyleId) this.getMatrix();
    },
    managerId() {
      if (this.managerId) this.getFundList();
    },

    addColCount() {
      this.showColumnDialog = true;
    },
    addRowCount() {
      this.editRow(null, -1);
    },
    saveMatrixCount() {
      this.updateMatrix();
    },

    areAllRowsValid: {
      immediate: true,
      handler() {
        this.$emit('validateRows', this.areAllRowsValid);
      },
    },
    areAllYearsDifferent: {
      immediate: true,
      handler() {
        this.$emit('validateYears', this.areAllYearsDifferent);
      },
    },
    isSaveDisabled: {
      immediate: true,
      handler() {
        this.$emit('validateSave', this.isSaveDisabled);
      },
    },
    isAddRowDisabled: {
      immediate: true,
      handler() {
        this.$emit('validateAddRow', this.isAddRowDisabled);
      },
    },
  },

  created() {
    if (this.lifestyleId) this.getMatrix();
    this.getManagerList();
  },

  methods: {
    sortTable(matrix) {
      matrix.sort((a, b) => a[0].AgeToRetirement - b[0].AgeToRetirement);
    },

    editRow(row, index) {
      if (!this.showFormButtons) return;
      this.showDialog = true;
      if (row && index !== -1) {
        this.editedRowIndex = index;
        this.editedRow = JSON.parse(JSON.stringify(row));
        this.editedRowYTR = this.editedRow[0].AgeToRetirement;
      } else {
        this.editedRow = JSON.parse(JSON.stringify(this.defaultRow));
        this.editedRowIndex = -1;
        if (this.matrixList.length) {
          this.editedRowYTR = this.matrixList[this.matrixList.length - 1][0].AgeToRetirement + 1;
        } else {
          this.editedRowYTR = 1;
        }
      }
    },

    checkRowValues(row) {
      const sum = row.reduce((total, current) => (total += Number(current.Percent)), 0);
      if (sum == 100) return true;
      return false;
    },

    saveRow() {
      this.isRowValid = this.checkRowValues(this.editedRow);

      if (this.isRowValid) {
        this.showDialog = false;

        this.editedRow.forEach((cell) => (cell.AgeToRetirement = Number(this.editedRowYTR)));
        if (this.editedRowIndex === -1) {
          this.matrixList.push(this.editedRow);
        } else {
          this.matrixList.splice(this.editedRowIndex, 1, this.editedRow);
        }
        this.sortTable(this.matrixList);
      }
    },

    cancelChages() {
      this.resetEdit;
    },

    resetEdit() {
      this.showDialog = false;
      this.editedRowIndex = -1;
      this.editedRow = JSON.parse(JSON.stringify(this.defaultRow));
    },

    addColumn() {
      this.isNewColumnValid = this.$refs.colForm.validate();
      if (!this.isNewColumnValid) return;
      if (!this.matrixList.length) this.matrixList.push([]);
      this.matrixList.forEach((row, index) => {
        row.push({
          FundName: this.fundList.find((fund) => fund.PortfolioId === this.fundId).PortfolioName,
          ManagerId: this.managerId,
          ManagerName: this.managerList.find((manager) => manager.ManagerId === this.managerId)
            .ManagerName,
          ColIndex: row.length + 1,
          RowIndex: index + 1,
          LifeStyleRowId: 0,
          LifestyleId: this.lifestyleId,
          FundId: this.fundId,
          Percent: 0,
          AgeToRetirement: row[0] ? row[0].AgeToRetirement : 1,
        });
      });

      this.showColumnDialog = false;
      this.managerId = null;
      this.fundId = null;
    },

    async deleteRow(index) {
      await this.$refs.confirmationDialog.open(
        'Confirmation',
        'Are you sure you wish to delete this row?'
      );
      this.matrixList.splice(index, 1);
      this.resetEdit();
    },

    async deleteColumn(colIndex) {
      await this.$refs.confirmationDialog.open(
        'Confirmation',
        'Are you sure you wish to delete this column?'
      );
      if (this.matrixList[0].length === 1) return (this.matrixList = []);
      this.matrixList.forEach((row) => {
        const index = row.findIndex((cell) => cell.ColIndex === colIndex);
        row.splice(index, 1);
      });
    },

    async getMatrix() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          `/lifestyle/GetMatrix?lifestyleId=${this.lifestyleId}`
        );

        this.matrixList = response.data;
        if (this.matrixList && this.matrixList.length) this.sortTable(this.matrixList);
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async getManagerList() {
      this.isManagerIdLoading = true;
      try {
        const response = await this.$api.get('/Funds/GetManagerList');

        this.managerList = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.isManagerIdLoading = false;
      }
    },

    async getFundList() {
      this.isFundIdLoading = true;
      try {
        const response = await this.$api.get('/Funds/GetFundList?managerId=' + this.managerId);

        this.fundList = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.isFundIdLoading = false;
      }
    },

    async updateMatrix() {
      const savingMatrix = {
        LifestyleId: this.lifestyleId,
        Matrix: this.matrixList,
      };

      this.loading = true;
      try {
        await this.$api.post('/lifestyle/UpdateMatrix', savingMatrix);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Matrix is updated successfully',
        });
        this.getMatrix();
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
th {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  height: 32px;
}
td {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
  text-align: center;
  height: 32px;
}
tr:nth-child(n + 2):hover {
  cursor: pointer;
  background-color: #eeeeee;
}
tr:nth-child(even) {
  background-color: #f4f4f4;
}
.bd-x {
  border-left: thin solid rgba(0, 0, 0, 0.12);
  border-right: thin solid rgba(0, 0, 0, 0.12);
}
.bd-l {
  border-left: thin solid rgba(0, 0, 0, 0.12);
}
.bd-r {
  border-right: thin solid rgba(0, 0, 0, 0.12);
}
.bd-b {
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.bd-t {
  border-top: thin solid rgba(0, 0, 0, 0.12);
}
</style>
